import type { Region } from './types';

// Viewport is 1000x1000. Regions arranged hexagonally around center
// (500,500). Pushed further out (was D=280) so the wash circles don't
// overlap and there's room for landmarks to live cleanly inside each
// region without colliding with neighbours.
const D = 330;
function vertex(idx: number): [number, number] {
  // Servant at top, then clockwise.
  const angle = (-Math.PI / 2) + (idx * (Math.PI * 2)) / 6;
  return [500 + Math.cos(angle) * D, 500 + Math.sin(angle) * D];
}

export const REGIONS: Region[] = [
  {
    id: 'servant',
    name: 'the servant valley',
    cx: vertex(0)[0],
    cy: vertex(0)[1],
    accent: '#E0C896',
    brief: 'cultivated land of usefulness and care',
    long:
      'a long valley of terraced fields and a great house at its center. the practice here is composed of small acts: tea poured, chores tended, comfort built around another. usefulness is the language; the gift is the noticing. the valley is fed by quiet rivers — patience, attention, and the pleasure of doing a thing well.',
    landmarks: ['negotiation-library', 'collar-cathedral', 'aftercare-springs'],
  },
  {
    id: 'devotee',
    name: 'the devotee mountain',
    cx: vertex(1)[0],
    cy: vertex(1)[1],
    accent: '#FFB347',
    brief: 'a peak with a small temple at its summit',
    long:
      'a single mountain rising sharply, with a small temple at its peak and winding paths up its sides. the practice here is ceremonial: thresholds, postures, repeated phrases, slow time. ritual is the architecture; devotion is the climate. pilgrims arrive often; many stay.',
    landmarks: ['threshold-bell', 'contract-scriptorium', 'collar-cathedral'],
  },
  {
    id: 'sensualist',
    name: 'the sensualist coast',
    cx: vertex(2)[0],
    cy: vertex(2)[1],
    accent: '#E8AAB6',
    brief: 'cliffs, warm waters, and steaming springs',
    long:
      'a long shoreline of cliffs and coves, with hot springs steaming up among the rocks. the practice here is embodied: sensation as gateway, the body as the doorway in. silk, rope, palm, breath, temperature — these are the local dialects. words travel slowly here; bodies speak first.',
    landmarks: ['rope-shrine', 'aftercare-springs'],
  },
  {
    id: 'primal',
    name: 'the primal forest',
    cx: vertex(3)[0],
    cy: vertex(3)[1],
    accent: '#E0626A',
    brief: 'wild paths, a watering hole at the heart',
    long:
      'old trees, no signs, paths that change overnight. at the center, a watering hole where everything comes to drink. the practice here is older than the alphabet: chase, claim, the body that knows what the other body does. negotiation happens at the edge of the forest; surrender happens inside it.',
    landmarks: ['safeword-bell-tower'],
  },
  {
    id: 'brat',
    name: 'the brat market',
    cx: vertex(4)[0],
    cy: vertex(4)[1],
    accent: '#F5E6D3',
    brief: 'twisting alleys, many doors, much laughter',
    long:
      'a market town with twisting alleys and many doors, where the practice is play. the surrender is sweeter when it has been earned. soft compliance is boring; the fight is the foreplay. the market has its rules; visitors learn them by stumbling. dominants who arrive here without flexibility leave quickly.',
    landmarks: ['safeword-bell-tower', 'negotiation-library', 'munch-tavern'],
  },
  {
    id: 'little',
    name: 'the little cove',
    cx: vertex(5)[0],
    cy: vertex(5)[1],
    accent: '#C8B89A',
    brief: 'cottages, gardens, lanterns along the path',
    long:
      'a sheltered cove with small cottages, walled gardens, and lanterns lining the paths after dusk. the practice here is gentle: structure that holds, tenderness that protects, the world made smaller by choice. caregiving is the climate; the heaviness of adult life is allowed to be put down for a while.',
    landmarks: ['aftercare-springs'],
  },
];
